import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  RotateCcw, Plus, CheckCircle, AlertCircle, Loader, X,
  Filter, RotateCcw as Reset, Clock, Check, XCircle, Package,
  Edit2, Trash2, Power, ShieldCheck, ChevronRight, Search
} from 'lucide-react';
import {
  getRetours, createRetour, approuverRetour, rejeterRetour,
  completerRetour, deleteRetour,
  getPolitiquesRetour, createPolitiqueRetour, updatePolitiqueRetour,
  deletePolitiqueRetour, togglePolitiqueRetour,
  getVentes,
} from '../services/api';
import './RetoursVentes.css';

const fmt     = (n) => new Intl.NumberFormat('fr-FR').format(n || 0) + ' XOF';
const fdate   = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

const STATUTS = {
  en_attente: { label: 'En attente', color: '#f59e0b', bg: '#fffbeb' },
  approuve:   { label: 'Approuvé',   color: '#3b82f6', bg: '#eff6ff' },
  rejete:     { label: 'Rejeté',     color: '#dc2626', bg: '#fee2e2' },
  complete:   { label: 'Complété',   color: '#16a34a', bg: '#dcfce7' },
};
const MOTIFS = {
  defaut:           'Défaut produit',
  insatisfaction:   'Insatisfaction',
  erreur_commande:  'Erreur commande',
  produit_endommage:'Produit endommagé',
  autre:            'Autre',
};
const TYPES_REMB = {
  remboursement_especes: 'Remboursement espèces',
  avoir:  'Avoir client',
  echange:'Échange',
};
const COND_PROD = { neuf:'Neuf', bon_etat:'Bon état', tout_etat:'Tout état', mauvais_etat:'Mauvais état' };
const EMPTY_POLITIQUE = { nom:'', description:'', delaiJours:14, conditionsProduit:'bon_etat', typeRemboursement:'remboursement_especes', pourcentageRemboursement:100, motifObligatoire:true };

export default function RetoursVentes() {
  const location = useLocation();
  const params   = new URLSearchParams(location.search);
  const venteIdParam = params.get('vente');

  const [tab, setTab] = useState('retours');

  // ── Retours state ──
  const [retours,     setRetours]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [submitting,  setSubmitting]  = useState(false);
  const [toast,       setToast]       = useState(null);

  const [filterStatut, setFilterStatut] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  // Modals retours
  const [createModal,   setCreateModal]   = useState(false);
  const [detailModal,   setDetailModal]   = useState(null);
  const [approuveModal, setApprouveModal] = useState(null);
  const [rejeteModal,   setRejeteModal]   = useState(null);
  const [completeModal, setCompleteModal] = useState(null);

  // Create return form
  const [venteSearch,     setVenteSearch]     = useState('');
  const [ventes,          setVentes]          = useState([]);
  const [venteDropdown,   setVenteDropdown]   = useState(false);
  const [selectedVente,   setSelectedVente]   = useState(null);
  const [lignesRetour,    setLignesRetour]     = useState([]);
  const [motif,           setMotif]           = useState('defaut');
  const [motifDetail,     setMotifDetail]     = useState('');
  const [typeRemb,        setTypeRemb]        = useState('remboursement_especes');
  const [notes,           setNotes]           = useState('');
  const [motifRejet,      setMotifRejet]      = useState('');
  const [notesComplete,   setNotesComplete]   = useState('');

  // ── Politiques state ──
  const [politiques,   setPolitiques]   = useState([]);
  const [polLoading,   setPolLoading]   = useState(false);
  const [polModal,     setPolModal]     = useState(null); // null | 'create' | politique obj
  const [polForm,      setPolForm]      = useState(EMPTY_POLITIQUE);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => { loadRetours(); loadVentes(); }, []);         // eslint-disable-line
  useEffect(() => { if (tab === 'politiques') loadPolitiques(); }, [tab]); // eslint-disable-line

  // Pre-open create modal if vente id in URL
  useEffect(() => {
    if (venteIdParam && ventes.length > 0) {
      const v = ventes.find(v => v._id === venteIdParam);
      if (v) { selectVente(v); setCreateModal(true); }
    }
  }, [venteIdParam, ventes]); // eslint-disable-line

  const loadRetours = async () => {
    setLoading(true);
    try {
      const { data } = await getRetours();
      setRetours(data.data || []);
    } catch { showToast('error', 'Erreur de chargement'); }
    finally { setLoading(false); }
  };

  const loadVentes = async () => {
    try {
      const { data } = await getVentes();
      setVentes((data.data || []).filter(v => v.statut !== 'annulee'));
    } catch { /* silencieux */ }
  };

  const loadPolitiques = async () => {
    setPolLoading(true);
    try {
      const { data } = await getPolitiquesRetour();
      setPolitiques(data.data || []);
    } catch { showToast('error', 'Erreur chargement politiques'); }
    finally { setPolLoading(false); }
  };

  // ── Filters ──
  const filtered = useMemo(() => {
    let r = retours;
    if (filterStatut) r = r.filter(x => x.statut === filterStatut);
    if (filterSearch) {
      const s = filterSearch.toLowerCase();
      r = r.filter(x => x.numeroRetour?.toLowerCase().includes(s) ||
        x.vente?.numeroFacture?.toLowerCase().includes(s) ||
        x.client?.nom?.toLowerCase().includes(s));
    }
    return r;
  }, [retours, filterStatut, filterSearch]);

  const nbAttente  = useMemo(() => retours.filter(r => r.statut === 'en_attente').length,  [retours]);
  const nbApprouve = useMemo(() => retours.filter(r => r.statut === 'approuve').length,    [retours]);
  const totalRembourse = useMemo(() => retours.filter(r => r.statut === 'complete').reduce((s, r) => s + (r.montantTotal || 0), 0), [retours]);

  // ── Vente search ──
  const ventesFiltered = useMemo(() => {
    if (!venteSearch.trim()) return ventes.slice(0, 10);
    const s = venteSearch.toLowerCase();
    return ventes.filter(v => v.numeroFacture?.toLowerCase().includes(s)).slice(0, 10);
  }, [ventes, venteSearch]);

  const selectVente = (v) => {
    setSelectedVente(v);
    setVenteSearch(v.numeroFacture);
    setVenteDropdown(false);
    setLignesRetour(v.details.map(d => ({
      produitId:   d.produit._id || d.produit,
      nomProduit:  d.nomProduit || '',
      prixUnitaire:d.prixUnitaire,
      quantiteVendue: d.quantite,
      quantite:    0,
      etatProduit: 'bon_etat',
      inclus:      false,
    })));
  };

  const resetCreateModal = () => {
    setSelectedVente(null); setVenteSearch(''); setVenteDropdown(false);
    setLignesRetour([]); setMotif('defaut'); setMotifDetail(''); setTypeRemb('remboursement_especes'); setNotes('');
    setCreateModal(false);
  };

  const montantEstime = useMemo(() =>
    lignesRetour.filter(l => l.inclus && l.quantite > 0)
      .reduce((s, l) => s + l.prixUnitaire * l.quantite, 0),
    [lignesRetour]);

  // ── Handlers retours ──
  const handleCreate = async (e) => {
    e.preventDefault();
    const lignes = lignesRetour.filter(l => l.inclus && l.quantite > 0);
    if (!selectedVente) { showToast('error', 'Sélectionnez une vente'); return; }
    if (lignes.length === 0) { showToast('error', 'Sélectionnez au moins un produit à retourner'); return; }
    setSubmitting(true);
    try {
      await createRetour({
        venteId: selectedVente._id,
        lignes: lignes.map(l => ({ produitId: l.produitId, quantite: l.quantite, etatProduit: l.etatProduit })),
        motif, motifDetail, typeRemboursement: typeRemb, notesInternes: notes,
      });
      showToast('success', 'Demande de retour créée');
      resetCreateModal();
      loadRetours();
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const handleApprouver = async () => {
    setSubmitting(true);
    try {
      await approuverRetour(approuveModal._id);
      showToast('success', 'Retour approuvé');
      setApprouveModal(null); loadRetours();
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const handleRejeter = async () => {
    if (!motifRejet.trim()) { showToast('error', 'Motif de rejet requis'); return; }
    setSubmitting(true);
    try {
      await rejeterRetour(rejeteModal._id, { motifRejet });
      showToast('success', 'Retour rejeté');
      setRejeteModal(null); setMotifRejet(''); loadRetours();
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const handleCompleter = async () => {
    setSubmitting(true);
    try {
      await completerRetour(completeModal._id, { notesInternes: notesComplete });
      showToast('success', 'Retour complété — stock restauré');
      setCompleteModal(null); setNotesComplete(''); loadRetours();
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette demande de retour ?')) return;
    try { await deleteRetour(id); showToast('success', 'Retour supprimé'); loadRetours(); }
    catch (err) { showToast('error', err.response?.data?.message || 'Erreur'); }
  };

  // ── Handlers politiques ──
  const handleSavePolitique = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (polModal === 'create') {
        await createPolitiqueRetour(polForm);
        showToast('success', 'Politique créée');
      } else {
        await updatePolitiqueRetour(polModal._id, polForm);
        showToast('success', 'Politique modifiée');
      }
      setPolModal(null); loadPolitiques();
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const handleDeletePolitique = async (id) => {
    if (!window.confirm('Supprimer cette politique ?')) return;
    try { await deletePolitiqueRetour(id); showToast('success', 'Politique supprimée'); loadPolitiques(); }
    catch (err) { showToast('error', err.response?.data?.message || 'Erreur'); }
  };

  const handleTogglePolitique = async (id) => {
    try { await togglePolitiqueRetour(id); loadPolitiques(); }
    catch (err) { showToast('error', 'Erreur'); }
  };

  return (
    <div className="rt-page">

      {/* Toast */}
      {toast && (
        <div className={`rt-toast rt-toast--${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={13}/> : <AlertCircle size={13}/>}
          {toast.text}
        </div>
      )}

      {/* ── Header ── */}
      <div className="rt-header">
        <div className="rt-header-left">
          <div className="rt-header-icon"><RotateCcw size={20}/></div>
          <div>
            <h1 className="rt-header-title">Retours & Politiques</h1>
            <p className="rt-header-sub">Gestion des retours produits et politiques de remboursement</p>
          </div>
        </div>
        <button className="rt-new-btn" onClick={() => setCreateModal(true)}>
          <Plus size={15}/> Nouveau retour
        </button>
      </div>

      {/* ── Tabs ── */}
      <div className="rt-tabs">
        <button className={`rt-tab ${tab === 'retours' ? 'rt-tab--active' : ''}`} onClick={() => setTab('retours')}>
          <RotateCcw size={14}/> Retours {retours.length > 0 && <span className="rt-tab-count">{retours.length}</span>}
        </button>
        <button className={`rt-tab ${tab === 'politiques' ? 'rt-tab--active' : ''}`} onClick={() => setTab('politiques')}>
          <ShieldCheck size={14}/> Politiques de retour
        </button>
      </div>

      <div className="rt-content">

        {/* ══════════════ TAB : RETOURS ══════════════ */}
        {tab === 'retours' && (
          <>
            {/* KPI */}
            <div className="rt-kpi-row">
              {[
                { label: 'Total retours',    value: retours.length,    color:'#6366f1', bg:'#eef2ff', icon: RotateCcw  },
                { label: 'En attente',       value: nbAttente,         color:'#f59e0b', bg:'#fffbeb', icon: Clock      },
                { label: 'Approuvés',        value: nbApprouve,        color:'#3b82f6', bg:'#eff6ff', icon: Check      },
                { label: 'Remboursé total',  value: fmt(totalRembourse),color:'#16a34a', bg:'#dcfce7', icon: CheckCircle},
              ].map((k, i) => {
                const Icon = k.icon;
                return (
                  <div key={i} className="rt-kpi" style={{ borderTopColor: k.color }}>
                    <div className="rt-kpi-icon" style={{ background: k.bg, color: k.color }}><Icon size={14}/></div>
                    <div className="rt-kpi-val">{k.value}</div>
                    <div className="rt-kpi-label">{k.label}</div>
                  </div>
                );
              })}
            </div>

            {/* Filters */}
            <div className="rt-filters">
              <Filter size={13} style={{ color:'#94a3b8', flexShrink:0 }}/>
              <div className="rt-search-wrap">
                <Search size={13} className="rt-search-icon"/>
                <input className="rt-search" type="text" placeholder="N° retour, facture, client…"
                  value={filterSearch} onChange={e => setFilterSearch(e.target.value)}/>
              </div>
              <select className="rt-select" value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
                <option value="">Tous les statuts</option>
                <option value="en_attente">En attente</option>
                <option value="approuve">Approuvé</option>
                <option value="rejete">Rejeté</option>
                <option value="complete">Complété</option>
              </select>
              <button className="rt-reset-btn" onClick={() => { setFilterStatut(''); setFilterSearch(''); }}>
                <Reset size={13}/> Réinitialiser
              </button>
              <span className="rt-count">{filtered.length} résultat{filtered.length !== 1 ? 's' : ''}</span>
            </div>

            {/* Table */}
            {loading ? (
              <div className="rt-loading-inline"><Loader size={20} className="rt-spin"/> Chargement…</div>
            ) : filtered.length === 0 ? (
              <div className="rt-empty">
                <div className="rt-empty-icon"><RotateCcw size={32}/></div>
                <h3>Aucun retour</h3>
                <p>Créez votre première demande de retour</p>
              </div>
            ) : (
              <div className="rt-card">
                <table className="rt-table">
                  <thead>
                    <tr>
                      <th>N° Retour</th>
                      <th>Vente</th>
                      <th>Client</th>
                      <th>Motif</th>
                      <th>Remboursement</th>
                      <th>Montant</th>
                      <th>Date</th>
                      <th>Statut</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(r => {
                      const st = STATUTS[r.statut] || STATUTS.en_attente;
                      return (
                        <tr key={r._id}>
                          <td><span className="rt-num">{r.numeroRetour}</span></td>
                          <td><span className="rt-num">{r.vente?.numeroFacture || '—'}</span></td>
                          <td className="rt-client">{r.client?.nom || <span className="rt-muted">—</span>}</td>
                          <td><span className="rt-motif">{MOTIFS[r.motif] || r.motif}</span></td>
                          <td><span className="rt-type">{TYPES_REMB[r.typeRemboursement]}</span></td>
                          <td className="rt-amount">{fmt(r.montantTotal)}</td>
                          <td className="rt-date">{fdate(r.createdAt)}</td>
                          <td>
                            <span className="rt-badge" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                          </td>
                          <td>
                            <div className="rt-row-actions">
                              <button className="rt-btn rt-btn--view" title="Détail" onClick={() => setDetailModal(r)}>
                                <ChevronRight size={13}/>
                              </button>
                              {r.statut === 'en_attente' && (
                                <>
                                  <button className="rt-btn rt-btn--green" title="Approuver" onClick={() => setApprouveModal(r)}>
                                    <Check size={13}/>
                                  </button>
                                  <button className="rt-btn rt-btn--danger" title="Rejeter" onClick={() => { setRejeteModal(r); setMotifRejet(''); }}>
                                    <XCircle size={13}/>
                                  </button>
                                  <button className="rt-btn rt-btn--trash" title="Supprimer" onClick={() => handleDelete(r._id)}>
                                    <Trash2 size={13}/>
                                  </button>
                                </>
                              )}
                              {r.statut === 'approuve' && (
                                <>
                                  <button className="rt-btn rt-btn--complete" title="Compléter" onClick={() => { setCompleteModal(r); setNotesComplete(''); }}>
                                    <Package size={13}/>
                                  </button>
                                  <button className="rt-btn rt-btn--danger" title="Rejeter" onClick={() => { setRejeteModal(r); setMotifRejet(''); }}>
                                    <XCircle size={13}/>
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ══════════════ TAB : POLITIQUES ══════════════ */}
        {tab === 'politiques' && (
          <>
            <div className="rt-pol-topbar">
              <p className="rt-pol-desc">Les politiques définissent les conditions de retour applicables à vos ventes.</p>
              <button className="rt-new-btn" onClick={() => { setPolModal('create'); setPolForm(EMPTY_POLITIQUE); }}>
                <Plus size={15}/> Nouvelle politique
              </button>
            </div>

            {polLoading ? (
              <div className="rt-loading-inline"><Loader size={20} className="rt-spin"/> Chargement…</div>
            ) : politiques.length === 0 ? (
              <div className="rt-empty">
                <div className="rt-empty-icon"><ShieldCheck size={32}/></div>
                <h3>Aucune politique</h3>
                <p>Créez vos politiques de retour pour les appliquer aux demandes</p>
              </div>
            ) : (
              <div className="rt-pol-grid">
                {politiques.map(p => (
                  <div key={p._id} className={`rt-pol-card ${!p.actif ? 'rt-pol-card--inactive' : ''}`}>
                    <div className="rt-pol-card-head">
                      <div className="rt-pol-name">{p.nom}</div>
                      <span className={`rt-pol-badge ${p.actif ? 'rt-pol-badge--on' : 'rt-pol-badge--off'}`}>
                        {p.actif ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {p.description && <p className="rt-pol-desc-text">{p.description}</p>}
                    <div className="rt-pol-details">
                      <div className="rt-pol-detail"><span>Délai</span><strong>{p.delaiJours} jours</strong></div>
                      <div className="rt-pol-detail"><span>Condition produit</span><strong>{COND_PROD[p.conditionsProduit]}</strong></div>
                      <div className="rt-pol-detail"><span>Remboursement</span><strong>{TYPES_REMB[p.typeRemboursement]}</strong></div>
                      <div className="rt-pol-detail"><span>Taux</span><strong>{p.pourcentageRemboursement}%</strong></div>
                    </div>
                    <div className="rt-pol-actions">
                      <button className="rt-pol-btn rt-pol-btn--edit" onClick={() => { setPolModal(p); setPolForm({ nom:p.nom, description:p.description||'', delaiJours:p.delaiJours, conditionsProduit:p.conditionsProduit, typeRemboursement:p.typeRemboursement, pourcentageRemboursement:p.pourcentageRemboursement, motifObligatoire:p.motifObligatoire }); }}>
                        <Edit2 size={13}/> Modifier
                      </button>
                      <button className={`rt-pol-btn ${p.actif ? 'rt-pol-btn--deactivate' : 'rt-pol-btn--activate'}`} onClick={() => handleTogglePolitique(p._id)}>
                        <Power size={13}/> {p.actif ? 'Désactiver' : 'Activer'}
                      </button>
                      <button className="rt-pol-btn rt-pol-btn--delete" onClick={() => handleDeletePolitique(p._id)}>
                        <Trash2 size={13}/>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ══════════════ MODAL : Créer un retour ══════════════ */}
      {createModal && (
        <>
          <div className="rt-overlay" onClick={resetCreateModal}/>
          <div className="rt-modal rt-modal--lg">
            <div className="rt-modal-head">
              <div>
                <h2 className="rt-modal-title"><Plus size={15}/> Nouvelle demande de retour</h2>
              </div>
              <button className="rt-modal-close" onClick={resetCreateModal}><X size={16}/></button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="rt-modal-body">

                {/* Sélection vente */}
                <div className="rt-field">
                  <label>Vente concernée *</label>
                  <div className="rt-vente-search-wrap">
                    <Search size={13} className="rt-vente-search-icon"/>
                    <input className="rt-vente-search" type="text" placeholder="N° de facture…"
                      value={venteSearch}
                      onChange={e => { setVenteSearch(e.target.value); setVenteDropdown(true); setSelectedVente(null); setLignesRetour([]); }}
                      onFocus={() => setVenteDropdown(true)}/>
                  </div>
                  {venteDropdown && venteSearch && !selectedVente && ventesFiltered.length > 0 && (
                    <div className="rt-vente-dropdown">
                      {ventesFiltered.map(v => (
                        <div key={v._id} className="rt-vente-option"
                          onMouseDown={() => selectVente(v)}>
                          <span className="rt-num">{v.numeroFacture}</span>
                          <span className="rt-muted">{fdate(v.createdAt)} · {fmt(v.totalTTC)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Lignes de retour */}
                {selectedVente && lignesRetour.length > 0 && (
                  <div className="rt-field">
                    <div className="rt-lignes-header">
                      <label style={{ margin: 0 }}>Produits à retourner</label>
                      <div className="rt-lignes-actions">
                        <button type="button" className="rt-bulk-btn rt-bulk-btn--all"
                          onClick={() => {
                            // Tout retourner : inclus = true + quantité = quantité vendue
                            setLignesRetour(lignesRetour.map(l => ({ ...l, inclus: true, quantite: l.quantiteVendue })));
                          }}>
                          ✓ Tout retourner
                        </button>
                        <button type="button" className="rt-bulk-btn rt-bulk-btn--none"
                          onClick={() => {
                            setLignesRetour(lignesRetour.map(l => ({ ...l, inclus: false, quantite: 0 })));
                          }}>
                          Tout désélectionner
                        </button>
                      </div>
                    </div>
                    <div className="rt-lignes">
                      {lignesRetour.map((l, i) => (
                        <div key={i} className={`rt-ligne ${l.inclus ? 'rt-ligne--active' : ''}`}>
                          <label className="rt-ligne-check">
                            <input type="checkbox" checked={l.inclus}
                              onChange={e => {
                                const n = [...lignesRetour]; n[i] = { ...n[i], inclus: e.target.checked, quantite: e.target.checked ? l.quantiteVendue : 0 };
                                setLignesRetour(n);
                              }}/>
                          </label>
                          <div className="rt-ligne-info">
                            <span className="rt-ligne-name">{l.nomProduit || `Produit ${i+1}`}</span>
                            <span className="rt-ligne-price">{fmt(l.prixUnitaire)} · Vendu: {l.quantiteVendue}</span>
                          </div>
                          {l.inclus && (
                            <>
                              <div className="rt-ligne-qty">
                                <label>Qté</label>
                                <input type="number" min="1" max={l.quantiteVendue}
                                  value={l.quantite}
                                  onChange={e => { const n=[...lignesRetour]; n[i]={...n[i],quantite:Math.min(parseInt(e.target.value)||1,l.quantiteVendue)}; setLignesRetour(n); }}/>
                              </div>
                              <div className="rt-ligne-etat">
                                <label>État</label>
                                <select value={l.etatProduit}
                                  onChange={e => { const n=[...lignesRetour]; n[i]={...n[i],etatProduit:e.target.value}; setLignesRetour(n); }}>
                                  <option value="neuf">Neuf</option>
                                  <option value="bon_etat">Bon état</option>
                                  <option value="mauvais_etat">Mauvais état</option>
                                </select>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                    {montantEstime > 0 && (
                      <div className="rt-montant-estime">
                        Montant estimé : <strong>{fmt(montantEstime)}</strong>
                      </div>
                    )}
                  </div>
                )}

                <div className="rt-form-row2">
                  <div className="rt-field">
                    <label>Motif *</label>
                    <select value={motif} onChange={e => setMotif(e.target.value)} required>
                      {Object.entries(MOTIFS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div className="rt-field">
                    <label>Type de remboursement</label>
                    <select value={typeRemb} onChange={e => setTypeRemb(e.target.value)}>
                      {Object.entries(TYPES_REMB).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                </div>

                <div className="rt-field">
                  <label>Détail du motif <span className="rt-opt">(optionnel)</span></label>
                  <textarea rows="2" placeholder="Description complémentaire…"
                    value={motifDetail} onChange={e => setMotifDetail(e.target.value)}/>
                </div>
                <div className="rt-field">
                  <label>Notes internes <span className="rt-opt">(optionnel)</span></label>
                  <textarea rows="2" placeholder="Notes pour l'équipe…"
                    value={notes} onChange={e => setNotes(e.target.value)}/>
                </div>

              </div>
              <div className="rt-modal-footer">
                <button type="button" className="rt-btn-cancel" onClick={resetCreateModal}>Annuler</button>
                <button type="submit" className="rt-btn-confirm" disabled={submitting || !selectedVente}>
                  {submitting ? <><Loader size={13} className="rt-spin"/> Envoi…</> : <><Plus size={13}/> Créer la demande</>}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* ══════════════ MODAL : Détail retour ══════════════ */}
      {detailModal && (
        <>
          <div className="rt-overlay" onClick={() => setDetailModal(null)}/>
          <div className="rt-modal">
            <div className="rt-modal-head">
              <div>
                <h2 className="rt-modal-title"><RotateCcw size={15}/> {detailModal.numeroRetour}</h2>
                <p className="rt-modal-sub">{detailModal.vente?.numeroFacture} · {fdate(detailModal.createdAt)}</p>
              </div>
              <button className="rt-modal-close" onClick={() => setDetailModal(null)}><X size={16}/></button>
            </div>
            <div className="rt-modal-body">
              <div className="rt-detail-grid">
                <div className="rt-detail-item"><span>Client</span><strong>{detailModal.client?.nom || '—'}</strong></div>
                <div className="rt-detail-item"><span>Motif</span><strong>{MOTIFS[detailModal.motif]}</strong></div>
                <div className="rt-detail-item"><span>Remboursement</span><strong>{TYPES_REMB[detailModal.typeRemboursement]}</strong></div>
                <div className="rt-detail-item"><span>Montant total</span><strong style={{color:'#6366f1'}}>{fmt(detailModal.montantTotal)}</strong></div>
              </div>
              {detailModal.motifDetail && <p className="rt-detail-note">{detailModal.motifDetail}</p>}
              <div className="rt-detail-section-title">Produits retournés</div>
              {detailModal.lignes.map((l, i) => (
                <div key={i} className="rt-detail-ligne">
                  <span>{l.nomProduit}</span>
                  <span>×{l.quantite} · {COND_PROD[l.etatProduit]}</span>
                  <span className="rt-amount">{fmt(l.montantRembourse)}</span>
                </div>
              ))}
              {detailModal.statut === 'rejete' && detailModal.motifRejet && (
                <div className="rt-alert rt-alert--error"><AlertCircle size={13}/> Rejet : {detailModal.motifRejet}</div>
              )}
              {detailModal.notesInternes && (
                <p className="rt-detail-note"><strong>Notes :</strong> {detailModal.notesInternes}</p>
              )}
            </div>
          </div>
        </>
      )}

      {/* ══════════════ MODAL : Approuver ══════════════ */}
      {approuveModal && (
        <>
          <div className="rt-overlay" onClick={() => setApprouveModal(null)}/>
          <div className="rt-modal rt-modal--sm">
            <div className="rt-modal-head">
              <h2 className="rt-modal-title"><Check size={15}/> Approuver le retour</h2>
              <button className="rt-modal-close" onClick={() => setApprouveModal(null)}><X size={16}/></button>
            </div>
            <div className="rt-modal-body">
              <p className="rt-confirm-text">Approuver le retour <strong>{approuveModal.numeroRetour}</strong> ?</p>
              <p className="rt-confirm-sub">Le retour passera en statut "Approuvé". Vous pourrez ensuite le compléter pour restaurer le stock.</p>
            </div>
            <div className="rt-modal-footer">
              <button className="rt-btn-cancel" onClick={() => setApprouveModal(null)}>Annuler</button>
              <button className="rt-btn-confirm rt-btn-confirm--green" onClick={handleApprouver} disabled={submitting}>
                {submitting ? <><Loader size={13} className="rt-spin"/> …</> : <><Check size={13}/> Approuver</>}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ══════════════ MODAL : Rejeter ══════════════ */}
      {rejeteModal && (
        <>
          <div className="rt-overlay" onClick={() => setRejeteModal(null)}/>
          <div className="rt-modal rt-modal--sm">
            <div className="rt-modal-head">
              <h2 className="rt-modal-title" style={{color:'#dc2626'}}><XCircle size={15}/> Rejeter le retour</h2>
              <button className="rt-modal-close" onClick={() => setRejeteModal(null)}><X size={16}/></button>
            </div>
            <div className="rt-modal-body">
              <div className="rt-field">
                <label>Motif de rejet *</label>
                <textarea rows="3" placeholder="Expliquez pourquoi ce retour est rejeté…"
                  value={motifRejet} onChange={e => setMotifRejet(e.target.value)} required/>
              </div>
            </div>
            <div className="rt-modal-footer">
              <button className="rt-btn-cancel" onClick={() => setRejeteModal(null)}>Annuler</button>
              <button className="rt-btn-confirm rt-btn-confirm--danger" onClick={handleRejeter} disabled={submitting || !motifRejet.trim()}>
                {submitting ? <><Loader size={13} className="rt-spin"/> …</> : <><XCircle size={13}/> Rejeter</>}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ══════════════ MODAL : Compléter ══════════════ */}
      {completeModal && (
        <>
          <div className="rt-overlay" onClick={() => setCompleteModal(null)}/>
          <div className="rt-modal rt-modal--sm">
            <div className="rt-modal-head">
              <h2 className="rt-modal-title"><Package size={15}/> Compléter le retour</h2>
              <button className="rt-modal-close" onClick={() => setCompleteModal(null)}><X size={16}/></button>
            </div>
            <div className="rt-modal-body">
              <div className="rt-confirm-box">
                <p className="rt-confirm-text">Compléter le retour <strong>{completeModal.numeroRetour}</strong> ?</p>
                <ul className="rt-confirm-list">
                  <li>Le stock des produits retournés sera <strong>restauré</strong></li>
                  <li>Montant à rembourser : <strong style={{color:'#6366f1'}}>{fmt(completeModal.montantTotal)}</strong></li>
                  <li>Mode : <strong>{TYPES_REMB[completeModal.typeRemboursement]}</strong></li>
                </ul>
              </div>
              <div className="rt-field" style={{marginTop:12}}>
                <label>Notes <span className="rt-opt">(optionnel)</span></label>
                <textarea rows="2" value={notesComplete} onChange={e => setNotesComplete(e.target.value)}/>
              </div>
            </div>
            <div className="rt-modal-footer">
              <button className="rt-btn-cancel" onClick={() => setCompleteModal(null)}>Annuler</button>
              <button className="rt-btn-confirm rt-btn-confirm--green" onClick={handleCompleter} disabled={submitting}>
                {submitting ? <><Loader size={13} className="rt-spin"/> Traitement…</> : <><Package size={13}/> Confirmer et restaurer le stock</>}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ══════════════ MODAL : Politique ══════════════ */}
      {polModal && (
        <>
          <div className="rt-overlay" onClick={() => setPolModal(null)}/>
          <div className="rt-modal">
            <div className="rt-modal-head">
              <h2 className="rt-modal-title">
                {polModal === 'create' ? <><Plus size={15}/> Nouvelle politique</> : <><Edit2 size={15}/> Modifier la politique</>}
              </h2>
              <button className="rt-modal-close" onClick={() => setPolModal(null)}><X size={16}/></button>
            </div>
            <form onSubmit={handleSavePolitique}>
              <div className="rt-modal-body">
                <div className="rt-field">
                  <label>Nom *</label>
                  <input type="text" required placeholder="Ex : Retour 14 jours"
                    value={polForm.nom} onChange={e => setPolForm({...polForm, nom: e.target.value})}/>
                </div>
                <div className="rt-field">
                  <label>Description <span className="rt-opt">(optionnel)</span></label>
                  <textarea rows="2" placeholder="Conditions générales…"
                    value={polForm.description} onChange={e => setPolForm({...polForm, description: e.target.value})}/>
                </div>
                <div className="rt-form-row2">
                  <div className="rt-field">
                    <label>Délai maximum (jours) *</label>
                    <input type="number" min="0" required
                      value={polForm.delaiJours} onChange={e => setPolForm({...polForm, delaiJours: parseInt(e.target.value)||0})}/>
                  </div>
                  <div className="rt-field">
                    <label>Taux de remboursement (%)</label>
                    <input type="number" min="0" max="100"
                      value={polForm.pourcentageRemboursement} onChange={e => setPolForm({...polForm, pourcentageRemboursement: parseInt(e.target.value)||100})}/>
                  </div>
                </div>
                <div className="rt-form-row2">
                  <div className="rt-field">
                    <label>Condition produit requise</label>
                    <select value={polForm.conditionsProduit} onChange={e => setPolForm({...polForm, conditionsProduit: e.target.value})}>
                      <option value="neuf">Neuf (emballage intact)</option>
                      <option value="bon_etat">Bon état</option>
                      <option value="tout_etat">Tout état</option>
                    </select>
                  </div>
                  <div className="rt-field">
                    <label>Type de remboursement</label>
                    <select value={polForm.typeRemboursement} onChange={e => setPolForm({...polForm, typeRemboursement: e.target.value})}>
                      <option value="remboursement_especes">Espèces</option>
                      <option value="avoir">Avoir client</option>
                      <option value="echange">Échange</option>
                    </select>
                  </div>
                </div>
                <label className="rt-checkbox-label">
                  <input type="checkbox" checked={polForm.motifObligatoire}
                    onChange={e => setPolForm({...polForm, motifObligatoire: e.target.checked})}/>
                  Motif obligatoire pour les demandes de retour
                </label>
              </div>
              <div className="rt-modal-footer">
                <button type="button" className="rt-btn-cancel" onClick={() => setPolModal(null)}>Annuler</button>
                <button type="submit" className="rt-btn-confirm" disabled={submitting}>
                  {submitting ? <><Loader size={13} className="rt-spin"/> …</> : <><Check size={13}/> {polModal === 'create' ? 'Créer' : 'Enregistrer'}</>}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
